import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useLocale } from '@hooks/useLocale';
import { useTranslation } from 'next-i18next';
import ReCAPTCHA from 'react-google-recaptcha';
import Button from '@components/ui/Button';
import FormFieldContainer from '@components/ui/FormFieldContainer';
import AlertInlineMessage from '@components/ui/AlertInlineMessage';
import LoadingSpinner from '@components/ui/LoadingSpinner';
import InputField, { InputType } from '@components/ui/InputField';
import { useCreate, useCheckEmail } from '@adapters/bigcommerce/hooks/customer';
import { usePasswordValidation } from '@hooks/usePasswordValidation';
import { validate } from 'email-validator';
import cn from 'classnames';
import PasswordValidation from '../PasswordValidation';

import styles from './SignUpForm.module.scss';

interface SignUpFormProps {
  onSignUp: () => void;
  submitText: string;
  existingError: string;
  isCheckoutRedirect?: boolean;
}

const SignUpForm = ({
  onSignUp,
  submitText,
  existingError,
  isCheckoutRedirect,
}: SignUpFormProps) => {
  const { t } = useTranslation('common');
  const locale = useLocale();
  const [email, setEmail] = useState('');
  const [emailIsValid, setEmailIsValid] = useState(false);
  const [password, setPassword] = useState({
    firstPassword: '',
    secondPassword: '',
  });
  const [passwordIsValid, setPasswordIsValid] = useState(false);
  const [passwordIsFocused, setPasswordIsFocused] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [dirty, setDirty] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [firstName, setFirst] = useState('');
  const [lastName, setLast] = useState('');
  const [preferredLanguage, setLanguage] = useState('en');
  const [emailConsent, setConsent] = useState(false);
  const [terms, setTerms] = useState(false);
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [phoneIsValid, setPhoneIsValid] = useState(false);
  const [phone, setPhone] = useState('');
  const [emailTooLong, setEmailTooLong] = useState(false);

  const create = useCreate();

  const checkEmail = useCheckEmail();

  useEffect(() => {
    setEmailIsValid(validate(email) && email.length <= 64);
  }, [email]);

  const [validLength, hasNumber, upperCase, lowerCase] = usePasswordValidation({
    firstPassword: password.firstPassword,
    secondPassword: password.secondPassword,
  });

  useEffect(() => {
    if (validLength && hasNumber && upperCase && lowerCase) {
      setPasswordIsValid(true);
    } else {
      setPasswordIsValid(false);
    }
  }, [validLength, hasNumber, upperCase, lowerCase]);

  useEffect(() => {
    const removeNonNumberCharacters = phone.replace(/[^\d]/g, '');
    if (removeNonNumberCharacters.length >= 4) {
      setPhoneIsValid(true);
    } else {
      setPhoneIsValid(false);
    }
  }, [phone]);

  const setFirstPass = (event: string) => {
    setPassword({ ...password, firstPassword: event });
  };

  const handleRadioChange = (event: string) => {
    setLanguage(event);
  };

  const handleConsent = () => {
    setConsent(!emailConsent);
  };

  const handleTerms = () => {
    setTerms(!terms);
  };

  const handleEmail = async () => {
    if (emailIsValid) {
      try {
        const recaptchaToken = await recaptchaRef.current?.executeAsync();
        recaptchaRef.current?.reset();
        const response = await checkEmail(email, recaptchaToken);
        if (response.exists) {
          setErrorMessage(t(existingError));
        } else {
          setErrorMessage('');
        }
      } catch (error: any) {
        console.error(`signup form error on checkemail: ${error}`);
        setErrorMessage(t('signup.error.unknown.error'));
      }
    }
  };

  const handleValidation = useCallback(() => {
    const isValid = terms && emailIsValid && passwordIsValid && phoneIsValid;
    if (dirty) {
      setDisabled(!isValid);
    }

    return isValid;
  }, [dirty, emailIsValid, passwordIsValid, phoneIsValid, terms]);

  useEffect(() => {
    handleValidation();
  }, [handleValidation]);
  const handleSignUp = async (e: React.SyntheticEvent<EventTarget>) => {
    e.preventDefault();

    if (!dirty) {
      setDirty(true);
    }

    const isValid = handleValidation();

    if (!isValid) return;
    if (!terms) return;

    try {
      setLoading(true);
      setErrorMessage('');

      const createRecaptchaToken = await recaptchaRef.current?.executeAsync();
      // create user
      await create({
        email,
        first_name: firstName,
        last_name: lastName,
        phone: phone.replace(/[^0-9]/g, ''),
        attributes: [
          {
            attribute_id: 1,
            attribute_value: preferredLanguage,
          },
          {
            attribute_id: 2,
            attribute_value: emailConsent ? 'true' : 'false',
          },
        ],
        authentication: {
          force_password_reset: false,
          new_password: password.firstPassword,
        },
        recaptchaToken: createRecaptchaToken,
      });

      // subscribe user
      if (emailConsent) {
        const response = await fetch(`/api/subscribe`, {
          body: JSON.stringify({ email, source: 'account_creation' }),
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        });

        if (response.status !== 200) {
          const parsed = await response.json();
          throw new Error(parsed.errors);
        }
      }

      setLoading(false);
      onSignUp();
    } catch (errors: any) {
      setErrorMessage(t('signup.error.unknown.error'));
      setLoading(false);
      recaptchaRef.current?.reset();
    }
  };

  return (
    <>
      {errorMessage && (
        <AlertInlineMessage
          classNames={styles.loginErrorMessage}
          messageType="ERROR"
        >
          <div dangerouslySetInnerHTML={{ __html: errorMessage }} />
        </AlertInlineMessage>
      )}

      <div className={styles.signupContainer}>
        <form onSubmit={handleSignUp} className={styles.formContainer}>
          <FormFieldContainer
            label={t('login.email.label')}
            htmlFor="signup-form-email"
            errorMessage={
              (dirty && !emailIsValid && t('signup.invalid.email')) ||
              (emailTooLong && t('signup.email.tooLong'))
            }
          >
            <InputField
              id="signup-form-email"
              type={InputType.email}
              value={email}
              onChange={(value) => {
                setEmail(value);
                setEmailTooLong(value.length > 64);
              }}
              onBlur={handleEmail}
              hasError={(dirty && !emailIsValid) || emailTooLong}
              optional
            />
          </FormFieldContainer>

          <FormFieldContainer
            label={t('login.password.label')}
            htmlFor="signup-form-password"
            errorMessage={
              dirty && !passwordIsValid && t('signup.invalid.password')
            }
          >
            <InputField
              id="signup-form-password"
              type={InputType.password}
              value={password.firstPassword}
              onFocus={() => setPasswordIsFocused(true)}
              onChange={(value) => setFirstPass(value)}
              onBlur={() => setPasswordIsFocused(false)}
              hasError={dirty && !passwordIsValid}
            />
          </FormFieldContainer>
          {passwordIsFocused && (
            <PasswordValidation password={password} needsMatch={false} />
          )}

          <div className={styles.formRow}>
            <div className={styles.formColumn}>
              <FormFieldContainer
                label={t('signup.firstName.label')}
                htmlFor="signup-form-first"
                errorMessage={
                  dirty && !firstName.length && t('signup.invalid.first.name')
                }
              >
                <InputField
                  id="signup-form-first"
                  type={InputType.text}
                  value={firstName}
                  onChange={(value) => setFirst(value)}
                  hasError={dirty && !firstName.length}
                  maxLength={30}
                />
              </FormFieldContainer>
            </div>

            <div className={styles.formColumn}>
              <FormFieldContainer
                label={t('signup.lastName.label')}
                htmlFor="signup-form-last"
                errorMessage={
                  dirty && !lastName.length && t('signup.invalid.last.name')
                }
              >
                <InputField
                  id="signup-form-last"
                  type={InputType.text}
                  value={lastName}
                  onChange={(value) => setLast(value)}
                  hasError={dirty && !lastName.length}
                  maxLength={40}
                />
              </FormFieldContainer>
            </div>
          </div>
          <FormFieldContainer
            label={t('signup.phone.label')}
            htmlFor="register-form-phone"
            errorMessage={
              dirty && !phoneIsValid && t('signup.invalid.phone.number')
            }
          >
            <InputField
              id="register-form-phone"
              type={InputType.tel}
              value={phone}
              onChange={(value) => setPhone(value)}
              hasError={dirty && !phoneIsValid}
            />
          </FormFieldContainer>

          <FormFieldContainer
            label={t('signup.language.preference')}
            htmlFor="signup-form-radio"
          >
            <div>
              <div className={styles.radioBttnRow} id="signup-form-radio">
                <input
                  id="signup-form-radio-english"
                  type="radio"
                  value="en"
                  checked={preferredLanguage === 'en'}
                  onChange={(value) => handleRadioChange(value.target.value)}
                />
                <label
                  className={styles.radioText}
                  htmlFor="signup-form-radio-english"
                >
                  {t('signup.language.preference.english')}
                </label>
              </div>
              <div className={styles.radioBttnRow}>
                <input
                  id="signup-form-radio-french"
                  type="radio"
                  value="fr"
                  checked={preferredLanguage === 'fr'}
                  onChange={(value) => handleRadioChange(value.target.value)}
                />
                <label
                  className={styles.radioText}
                  htmlFor="signup-form-radio-french"
                >
                  {t('signup.language.preference.french')}
                </label>
              </div>
            </div>
          </FormFieldContainer>

          <FormFieldContainer
            htmlFor="terms"
            errorMessage={dirty && !terms && t('signup.invalid.terms')}
          >
            <div className={styles.checkboxRow} key="terms-preference">
              <label htmlFor="terms" className={styles.checkboxText}>
                <input
                  id="terms"
                  type="checkbox"
                  checked={terms}
                  onChange={handleTerms}
                  className={styles.checkboxInput}
                />
                <div
                  className={styles.htmlText}
                  dangerouslySetInnerHTML={{ __html: t('signup.terms') }}
                />
              </label>
            </div>
          </FormFieldContainer>

          <div className={styles.checkboxRow} key="email-preference">
            <label htmlFor="email" className={styles.checkboxText}>
              <input
                type="checkbox"
                checked={emailConsent}
                onChange={handleConsent}
                id="email"
                className={styles.checkboxInput}
              />
              <div
                className={styles.htmlText}
                dangerouslySetInnerHTML={{ __html: t('signup.consent') }}
              />
            </label>
          </div>

          <ReCAPTCHA
            ref={recaptchaRef}
            size="invisible"
            badge="bottomleft"
            sitekey={`${process.env.NEXT_PUBLIC_RECAPTCHA_KEY}`}
          />

          <div className={styles.buttonRow}>
            <Button
              variant="primary"
              type="submit"
              disabled={disabled || loading}
              label={
                loading ? (
                  <LoadingSpinner
                    classNames={styles.loadingSpinner}
                    colour="var(--color-white)"
                  />
                ) : (
                  t(submitText)
                )
              }
              size="large"
              className={cn(styles.loginButton, {
                [styles.isDisabled]: loading,
              })}
            />
            <Button
              variant="secondary"
              label={t('signup.cancel')}
              size="large"
              href={
                isCheckoutRedirect
                  ? `/${locale}/auth/signin?redirect=checkout`
                  : `/${locale}`
              }
              className={styles.createAccountButton}
            />
          </div>
        </form>
      </div>
    </>
  );
};

export default SignUpForm;
